/**
 * filesCore (FILES-03) — the one set of file primitives behind every files
 * surface: the 360 "Files & photos" card, the DispatchPeek files section,
 * and the full dispatch page. One bucket, one metadata table, one upload
 * path; surfaces differ only in layout ([[feedback_declutter_best_in_class]]
 * — extend the surface that owns a concept, don't build a parallel one).
 *
 * Storage: private `customer-files` bucket; short-lived signed URLs for
 * viewing; archive-not-delete.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export const BUCKET = "customer-files";
export const MAX_BYTES = 25 * 1024 * 1024; // 25 MB per file (v1; admin-tunable later)

const PHOTO_EXT = ["jpg", "jpeg", "png", "gif", "bmp", "tif", "tiff", "heic"];

/** FILES-CALL-01: who put the file on the record. */
export type FileOrigin = "office" | "tech" | "customer" | "import";

export interface PegasusFile {
  id: string;
  custno: string;
  locno: string | null;
  dispatch: string | null;
  equipment_counter: number | null;
  agrmt_no: string | null;
  invoice: string | null;
  /** PAY-02c: set when this file is documentation attached to a payment refund. */
  refund_id: string | null;
  /** JOB-DOCS-01: set when this file belongs to an install job (heat load
   *  calcs, warranty certs, rebate paperwork). Keeps custno, so it shows on
   *  the customer's 360 Files too. */
  job_id: string | null;
  kind: "photo" | "document";
  /** PI-03: named document slot — warranty_cert | rebate_conf | permit_doc. */
  doc_tag: string | null;
  filename: string;
  storage_path: string;
  mime: string | null;
  size_bytes: number | null;
  caption: string | null;
  uploaded_by: string | null;
  created_at: string;
  archived: boolean;
  /** 'esc' = imported from ESC's DocAttach (FILES-02); keeps original links + dates. */
  source: "pegasus" | "esc";
  /** FILES-CALL-01: office (a login attached it) · tech (from the phone on a
   *  call) · customer (sent through the portal) · import (ESC DocAttach).
   *  Null on old rows reads as office. */
  origin: FileOrigin | null;
  /** JOB-DOCS-02: can the field crew see this job file? Null = the document
   *  type's default (Settings → Job Documents & Permits). */
  crew_visible: boolean | null;
  /** FILES-AI-01: what the AI read off the file (the classify-file function). */
  ai_tag?: string | null;
  ai_summary?: string | null;
  ai_data?: FileAiData | null;
  ai_at?: string | null;
  /** Who set doc_tag: the screen that filed it, the AI, or a person by hand. */
  tag_by?: "upload" | "ai" | "person" | null;
  /** FILES-EDIT-01: this file replaced that one (the old one is archived). */
  version_of?: string | null;
  /** FILES-EDIT-01: a marked-up copy of that file (the original stays). */
  markup_of?: string | null;
}

/** FILES-AI-01: the extras the AI returns. `ahri` = the AHRI certificate's fields. */
export interface FileAiData {
  confidence?: number;
  prices?: boolean;
  read?: "file" | "text" | "name";
  error?: string;
  ahri?: {
    ahri_ref: string; system_type: string | null; brand: string | null; series: string | null;
    outdoor_model: string | null; indoor_model: string | null; furnace_model: string | null;
    seer2: number | null; eer2: number | null; hspf2: number | null; afue: number | null;
    cooling_btuh: number | null; heating_btuh: number | null;
  };
  /** JOB-SOLD-01 (classify-file v4): a load calc report's numbers. */
  load_calc?: {
    heat_loss_btuh: number | null; heat_gain_btuh: number | null;
    sensible_btuh?: number | null; latent_btuh?: number | null; area_sqft?: number | null;
    design_heat_f?: number | null; design_cool_f?: number | null; software?: string | null; method?: string | null;
  };
  /** FILES-READ-02 (classify-file v4): a permit's numbers. */
  permit?: {
    permit_no: string | null; jurisdiction: string | null; trade: string | null; status: string | null;
    issued: string | null; expires: string | null; fee: number | null; work: string | null;
  };
  /** v3: blank, cut off or too blurry to read. */
  unreadable?: boolean;
}

/* ── JOB-DOCS-02 (Seth 2026-09-24: crews "review any documents and pictures
 * ... Plans, load calcs, pictures, etc. Scope of job") ────────────────────
 * Every job file has a TYPE (stored in doc_tag). Each type says whether the
 * crew sees it by default; any one file can be flipped. Office Only keeps
 * contracts and builder POs (prices) off the phone — NC crews see no prices.
 * Buildertrend frame: per-job folders, permission-based viewing. */
export interface JobDocType { key: string; label: string; crew: boolean }
export const JOB_DOC_TYPES_DEFAULT: JobDocType[] = [
  { key: "plans", label: "Plans", crew: true },
  { key: "load_calc", label: "Load Calc", crew: true },
  { key: "scope", label: "Scope", crew: true },
  { key: "photo", label: "Photos", crew: true },
  { key: "spec_sheet", label: "Spec Sheet", crew: true },
  { key: "permit_doc", label: "Permit", crew: true },
  { key: "sketch", label: "Sketch", crew: true },
  // AHRI-01: the AHRI certificate for the matched system (rebate paperwork).
  { key: "ahri_cert", label: "AHRI Certificate", crew: false },
  // FILES-JOB-01: the post-install card files warranty certificates here (they
  // read as "Office Only" before); rebate forms get their own shelf.
  { key: "warranty_cert", label: "Warranty", crew: false },
  { key: "rebate_conf", label: "Rebate Paperwork", crew: false },
  { key: "office", label: "Office Only", crew: false },
];
/** Types the office can't delete in Settings (other code files into them). */
export const JOB_DOC_TYPES_LOCKED = new Set(["photo", "permit_doc", "sketch", "ahri_cert", "warranty_cert", "office"]);
/** A saved list always keeps the built-ins other screens file into. */
export function withLockedTypes(list: JobDocType[]): JobDocType[] {
  const out = [...list];
  for (const t of JOB_DOC_TYPES_DEFAULT) {
    if (!JOB_DOC_TYPES_LOCKED.has(t.key) || out.some((x) => x.key === t.key)) continue;
    const office = out.findIndex((x) => x.key === "office");
    if (office >= 0) out.splice(office, 0, t); else out.push(t);
  }
  return out;
}
export function useJobDocTypes() {
  return useQuery({
    queryKey: ["org_setting", "job_doc_types"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<JobDocType[]> => {
      const { data } = await supabase.from("org_setting").select("value").eq("key", "job_doc_types").maybeSingle();
      const v = (data as { value: unknown } | null)?.value;
      const list = Array.isArray(v) ? (v as JobDocType[]).filter((t) => t && typeof t.key === "string" && typeof t.label === "string") : [];
      return list.length ? withLockedTypes(list) : JOB_DOC_TYPES_DEFAULT;
    },
  });
}
/** The file's type key — tagged, else a photo reads as Photos, anything else as Office Only. */
export function jobDocTypeOf(f: Pick<PegasusFile, "doc_tag" | "kind">, types: JobDocType[]): string {
  if (f.doc_tag && types.some((t) => t.key === f.doc_tag)) return f.doc_tag;
  return f.kind === "photo" ? "photo" : "office";
}
/** Effective crew visibility: the file's own switch, else a crew upload is
 *  visible (their own photos), else the type's default. */
export function crewCanSee(f: Pick<PegasusFile, "doc_tag" | "kind" | "crew_visible" | "origin">, types: JobDocType[]): boolean {
  if (f.crew_visible === true || f.crew_visible === false) return f.crew_visible;
  if (f.origin === "tech") return true;
  const key = jobDocTypeOf(f, types);
  return types.find((t) => t.key === key)?.crew ?? false;
}

export function useSetFileCrewVisible() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; crew_visible: boolean | null }) => {
      const { error } = await supabase.from("pegasus_file").update({ crew_visible: v.crew_visible }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => invalidateFileQueries(qc),
  });
}

export function useSetFileDocTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; doc_tag: string | null }) => {
      const { error } = await supabase.from("pegasus_file").update({ doc_tag: v.doc_tag, tag_by: "person" }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => invalidateFileQueries(qc),
  });
}

/** Every file-surface query starts with one of these prefixes; mutations
 *  invalidate both so an upload from the peek refreshes the 360 card too
 *  (the queryKey-mismatch lesson — keys fail silently). */
export function invalidateFileQueries(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["customer_files"] });
  qc.invalidateQueries({ queryKey: ["dispatch_files"] });
  // JOB-DOCS-02: the job pocket ("files","job",id) was missing here — an
  // upload on the job didn't show until the next background refetch.
  qc.invalidateQueries({ queryKey: ["files"] });
}

/** All of the customer's files; the 360 card scopes to the viewed property
 *  but still KNOWS about the rest (builders/apartments file permits
 *  per-property — a silent "no files" there is a lie). */
export function useCustomerFiles(custno: string) {
  return useQuery({
    queryKey: ["customer_files", custno],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PegasusFile[]> => {
      const { data, error } = await supabase
        .from("pegasus_file")
        .select("*")
        .eq("custno", custno)
        .eq("archived", false)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as PegasusFile[];
    },
  });
}

/** Files pinned to one dispatch — 81% of the ESC import is dispatch-linked. */
/** JOB-DOCS-01 — every live file pinned to one install job. */
export function useJobFiles(jobId: string | undefined) {
  return useQuery({
    queryKey: ["files", "job", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<PegasusFile[]> => {
      const { data, error } = await supabase
        .from("pegasus_file")
        .select("*")
        .eq("job_id", jobId!)
        .eq("archived", false)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as PegasusFile[];
    },
    staleTime: 15 * 1000,
  });
}

export function useDispatchFiles(dispatch: string | undefined) {
  return useQuery({
    queryKey: ["dispatch_files", dispatch],
    enabled: !!dispatch,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PegasusFile[]> => {
      const { data, error } = await supabase
        .from("pegasus_file")
        .select("*")
        .eq("dispatch", dispatch!)
        .eq("archived", false)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as PegasusFile[];
    },
  });
}

/** Signed view URLs for the listed files (1 hour, refreshed at 45 min). */
export function useSignedUrls(files: PegasusFile[]) {
  const paths = files.map((f) => f.storage_path);
  return useQuery({
    queryKey: ["customer_file_urls", paths.join("|")],
    enabled: paths.length > 0,
    staleTime: 45 * 60 * 1000,
    queryFn: async (): Promise<Map<string, string>> => {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrls(paths, 3600);
      if (error) throw error;
      const m = new Map<string, string>();
      for (const r of data ?? []) {
        if (r.signedUrl && r.path) m.set(r.path, r.signedUrl);
      }
      return m;
    },
  });
}

export interface UploadTarget {
  custno: string;
  locno?: string | null;
  dispatch?: string | null;
  /** PAY-02c: link the upload to a payment refund (documentation — the "why"). */
  refund_id?: string | null;
  /** JOB-DOCS-01: pin the upload to an install job. */
  job_id?: string | null;
  /** Optional caption stamped on every file in this batch (shows in Documents). */
  caption?: string | null;
  /** PI-03: tag every file in this batch into a named slot (warranty_cert …). */
  doc_tag?: string | null;
  /** FILES-CALL-01: where the upload comes from (default office; the phone says tech). */
  origin?: FileOrigin;
  /** JOB-DOCS-02: set the crew switch at upload (omit = follow the type default). */
  crew_visible?: boolean | null;
  /** FILES-EDIT-01: the upload is a new version of that file / a marked-up copy of it. */
  version_of?: string | null;
  markup_of?: string | null;
  /** FILES-AI-01: let the AI read it (default: yes when no type was given, or an
   *  AHRI certificate — its numbers get read off). */
  sort?: boolean;
}

/** FILES-AI-01: which new rows go to the AI — job files with no type yet, and
 *  the papers whose numbers get read off: AHRI certificates, load calcs, permits. */
const READ_OFF = new Set(["ahri_cert", "load_calc", "permit_doc"]);
function wantsSort(target: UploadTarget, row: { job_id: string | null; doc_tag: string | null }): boolean {
  if (target.sort === false) return false;
  if (target.sort === true) return true;
  return (!!row.job_id && !row.doc_tag) || READ_OFF.has(row.doc_tag ?? "");
}

/** FILES-AI-01: ask the AI what these files are (classify-file). It files the
 *  type only where nobody chose one; never throws — a miss keeps the safe
 *  default (a photo reads as Photos, a document as Office Only). */
export async function sortFiles(ids: string[], force = false): Promise<void> {
  for (let i = 0; i < ids.length; i += 8) {
    try {
      await supabase.functions.invoke("classify-file", { body: { file_ids: ids.slice(i, i + 8), force } });
    } catch { /* the file keeps its default type */ }
  }
}

/** The row a new upload makes (shared by both upload paths). */
function newFileRow(target: UploadTarget, file: File, path: string, by: string | null) {
  const ext = (file.name.split(".").pop() ?? "").toLowerCase();
  return {
    custno: target.custno,
    locno: target.locno ?? null,
    dispatch: target.dispatch ?? null,
    refund_id: target.refund_id ?? null,
    job_id: target.job_id ?? null,
    caption: target.caption ?? null,
    doc_tag: target.doc_tag ?? null,
    tag_by: target.doc_tag ? "upload" : null,
    kind: file.type.startsWith("image/") || PHOTO_EXT.includes(ext) ? "photo" : "document",
    filename: file.name,
    storage_path: path,
    mime: file.type || null,
    size_bytes: file.size,
    uploaded_by: by,
    origin: target.origin ?? "office",
    crew_visible: target.crew_visible ?? null,
    version_of: target.version_of ?? null,
    markup_of: target.markup_of ?? null,
  };
}

/** Upload one or more picked files against a target (customer, optionally
 *  pinned to a dispatch). Oversized files are skipped + reported, the rest
 *  proceed. */
/** The bare upload, reusable OUTSIDE react-query — e.g. the create-dispatch
 *  modal stages files (no dispatch id yet) then calls this once the dispatch
 *  exists, pinning them to it. Oversized files are skipped + reported. */
export async function uploadFilesTo(
  target: UploadTarget,
  files: File[],
): Promise<{ skipped: string[]; ids: string[]; sorting: Promise<void> }> {
  const { data: u } = await supabase.auth.getUser();
  const by = u?.user?.email ?? null;
  const skipped: string[] = [];
  const ids: string[] = [];
  const toSort: string[] = [];
  for (const file of files) {
    if (file.size > MAX_BYTES) {
      skipped.push(`${file.name} (over 25 MB)`);
      continue;
    }
    const safe = file.name.replace(/[^\w.\- ]+/g, "_");
    const path = `${target.custno}/${crypto.randomUUID()}-${safe}`;
    const { error: upErr } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, { contentType: file.type || undefined });
    if (upErr) throw upErr;
    // FILES-JOB-01: the database pins a call's file to its job (job_id comes back set).
    const { data: row, error: insErr } = await supabase.from("pegasus_file")
      .insert(newFileRow(target, file, path, by)).select("id, job_id, doc_tag").single();
    if (insErr) throw insErr;
    const r = row as { id: string; job_id: string | null; doc_tag: string | null };
    ids.push(r.id);
    if (wantsSort(target, r)) toSort.push(r.id);
  }
  // Fire and forget — the upload is done; the type fills in a few seconds later.
  const sorting = toSort.length ? sortFiles(toSort) : Promise.resolve();
  return { skipped, ids, sorting };
}

/** One file, and the row it made — for flows that link the file somewhere
 *  (INSTALLER-APP: an old unit's data-tag photo). Throws on a failed upload. */
export async function uploadOneFile(target: UploadTarget, file: File): Promise<PegasusFile> {
  if (file.size > MAX_BYTES) throw new Error(`${file.name} is over 25 MB`);
  const { data: u } = await supabase.auth.getUser();
  const safe = file.name.replace(/[^\w.\- ]+/g, "_");
  const path = `${target.custno}/${crypto.randomUUID()}-${safe}`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type || undefined });
  if (upErr) throw upErr;
  const { data, error } = await supabase.from("pegasus_file")
    .insert(newFileRow(target, file, path, u?.user?.email ?? null)).select("*").single();
  if (error) throw error;
  const row = data as PegasusFile;
  if (wantsSort(target, row)) void sortFiles([row.id]);
  return row;
}

export function useUploadFiles(target: UploadTarget) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) => uploadFilesTo(target, files),
    onSuccess: (r) => {
      invalidateFileQueries(qc);
      // The AI's pick lands a few seconds later — refresh once more when it does.
      void r.sorting.then(() => invalidateFileQueries(qc));
    },
  });
}

/** FILES-AI-01: sort (or re-sort) files by hand — the viewer's "Sort It Again",
 *  and the job's Documents card catching files that came in without a type. */
export function useSortFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { ids: string[]; force?: boolean }) => sortFiles(v.ids, !!v.force),
    onSettled: () => invalidateFileQueries(qc),
  });
}

/** One file, fresh — the viewer re-reads it after a rename or a type change. */
export function useFileRow(id: string | null | undefined) {
  return useQuery({
    queryKey: ["files", "one", id],
    enabled: !!id,
    staleTime: 10 * 1000,
    queryFn: async (): Promise<PegasusFile | null> => {
      const { data, error } = await supabase.from("pegasus_file").select("*").eq("id", id!).maybeSingle();
      if (error) throw error;
      return (data as PegasusFile | null) ?? null;
    },
  });
}

/** FILES-EDIT-01: rename — the name people see and the name a download saves
 *  as. The stored object keeps its path. The old extension stays on. */
export function useRenameFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; filename: string; was: string }) => {
      const name = keepExtension(v.filename, v.was);
      if (!name) throw new Error("A file needs a name.");
      const { error } = await supabase.from("pegasus_file").update({ filename: name }).eq("id", v.id);
      if (error) throw error;
      return name;
    },
    onSuccess: () => invalidateFileQueries(qc),
  });
}

/** "Heat Load" + "calc.pdf" → "Heat Load.pdf" (typing the extension is optional). */
export function keepExtension(name: string, was: string): string {
  const n = name.trim().replace(/[\\/:*?"<>|]+/g, "-").slice(0, 180);
  if (!n) return "";
  const ext = /\.[A-Za-z0-9]{1,6}$/.exec(was)?.[0] ?? "";
  if (!ext || n.toLowerCase().endsWith(ext.toLowerCase())) return n;
  return `${n}${ext}`;
}

/** FILES-EDIT-01: a new version — the new file takes the old one's place (same
 *  job / call / type / crew switch / caption) and the old one is archived, one
 *  click away from the new one. */
export function useReplaceFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { old: PegasusFile; file: File }) => {
      const o = v.old;
      const row = await uploadOneFile({
        custno: o.custno, locno: o.locno, dispatch: o.dispatch, job_id: o.job_id, refund_id: o.refund_id,
        caption: o.caption, doc_tag: o.doc_tag, crew_visible: o.crew_visible,
        origin: "office", version_of: o.id, sort: READ_OFF.has(o.doc_tag ?? "") ? true : !o.doc_tag,
      }, v.file);
      const { error } = await supabase.from("pegasus_file").update({ archived: true }).eq("id", o.id);
      if (error) throw error;
      return row;
    },
    onSuccess: () => invalidateFileQueries(qc),
  });
}

/** Download under the name people see (the storage answers "attachment"). */
export async function downloadFile(f: Pick<PegasusFile, "storage_path" | "filename">): Promise<void> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(f.storage_path, 120, { download: f.filename });
  if (error || !data?.signedUrl) throw error ?? new Error("No link");
  const a = document.createElement("a");
  a.href = data.signedUrl;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Can a browser show it inline? (HEIC / TIFF can't be drawn by Chrome.) */
export function isImageFile(f: Pick<PegasusFile, "kind" | "mime" | "filename">): boolean {
  const ext = (f.filename.split(".").pop() ?? "").toLowerCase();
  if (["heic", "heif", "tif", "tiff"].includes(ext) || /heic|heif|tiff/.test(f.mime ?? "")) return false;
  if ((f.mime ?? "").startsWith("image/")) return true;
  return ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext) || (f.kind === "photo" && !ext);
}
export function isPdfFile(f: Pick<PegasusFile, "mime" | "filename">): boolean {
  return (f.mime ?? "").includes("pdf") || f.filename.toLowerCase().endsWith(".pdf");
}

/** Archive-not-delete: the row flips a flag, the object stays in storage. */
export function useArchiveFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("pegasus_file")
        .update({ archived: true })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidateFileQueries(qc),
  });
}

export function prettySize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ------------------------------------------------------- FILES-CALL-01 */

/** The origin, with the old rows' fallback (null = the office; ESC = import). */
export function fileOrigin(f: Pick<PegasusFile, "origin" | "source">): FileOrigin {
  if (f.origin) return f.origin;
  return f.source === "esc" ? "import" : "office";
}

/** Who added it, in words a tech recognizes. Office rows name the login. */
export function fileWho(f: Pick<PegasusFile, "origin" | "source" | "uploaded_by" | "caption">): string {
  switch (fileOrigin(f)) {
    case "customer": return "the customer";
    case "import": return "ESC";
    case "tech": {
      const m = /\(([^()]+)\)\s*$/.exec(f.caption ?? "");
      return m ? m[1] : "the tech";
    }
    default: {
      const local = (f.uploaded_by ?? "").split("@")[0];
      return local ? `${local} (office)` : "the office";
    }
  }
}

/** "today 9:12 AM" · "yesterday" · "Tue" · "Sep 9" · "Sep 9, 2024". */
export function fileWhen(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (days <= 0) return `today ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
  if (days === 1) return "yesterday";
  if (days < 7) return d.toLocaleDateString("en-US", { weekday: "short" });
  return d.toLocaleDateString("en-US", d.getFullYear() === now.getFullYear()
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" });
}

export interface RequestMedia {
  id: number;
  created_at: string;
  media: { path: string; name?: string }[];
  request_type: string | null;
  symptoms: string | null;
}

/** A customer's still-open portal requests that carry photos/video and have
 *  not been booked into a call yet — the booking modal offers to attach them. */
export function useOpenRequestMedia(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["portal_request_media", custno],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<RequestMedia[]> => {
      const { data, error } = await supabase
        .from("portal_request")
        .select("id, created_at, media, request_type, symptoms")
        .eq("custno", custno as string)
        .eq("status", "new")
        .is("dispatch_ref", null)
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return ((data ?? []) as RequestMedia[]).filter((r) => Array.isArray(r.media) && r.media.length > 0);
    },
  });
}

/** Copy what the customer sent with a portal request onto the call the office
 *  just booked. The portal bucket is private and request-shaped; the phone
 *  reads pegasus_file — so the bytes move into the customer's files, pinned
 *  to the dispatch, labeled customer. Best effort per file; the request
 *  remembers the call either way. */
export async function attachRequestMediaTo(
  target: { custno: string; locno?: string | null; dispatch: string },
  req: RequestMedia,
): Promise<{ copied: number; failed: number }> {
  const { data: u } = await supabase.auth.getUser();
  const by = u?.user?.email ?? null;
  let copied = 0;
  let failed = 0;
  for (const m of req.media) {
    try {
      const { data: blob, error: dlErr } = await supabase.storage.from("portal-media").download(m.path);
      if (dlErr || !blob) throw dlErr ?? new Error("empty download");
      const name = (m.name || m.path.split("/").pop() || "photo").replace(/[^\w.\- ]+/g, "_");
      const path = `${target.custno}/${crypto.randomUUID()}-${name}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, blob, { contentType: blob.type || undefined });
      if (upErr) throw upErr;
      const ext = (name.split(".").pop() ?? "").toLowerCase();
      const isPhoto = blob.type.startsWith("image/") || PHOTO_EXT.includes(ext);
      const { error: insErr } = await supabase.from("pegasus_file").insert({
        custno: target.custno,
        locno: target.locno ?? null,
        dispatch: target.dispatch,
        kind: isPhoto ? "photo" : "document",
        filename: name,
        storage_path: path,
        mime: blob.type || null,
        size_bytes: blob.size,
        caption: "Sent by the customer with their service request",
        uploaded_by: by,
        origin: "customer",
      });
      if (insErr) throw insErr;
      copied += 1;
    } catch {
      failed += 1;
    }
  }
  await supabase.from("portal_request").update({ dispatch_ref: target.dispatch }).eq("id", req.id);
  return { copied, failed };
}
